import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Badge,
  DataTable,
  SkeletonBodyText,
  EmptyState,
  Banner,
  Box,
  InlineGrid,
} from '@shopify/polaris';
import { useDashboard, useConversions, useGoogleAdsConfig, useMetaConfig, useGA4Config, useTikTokConfig, usePinterestConfig, useSnapchatConfig } from '../hooks/api';
import { useTranslation, useLanguage } from '../i18n/LanguageContext';
import { localeMap } from '../i18n/translations';
import { UsageWarningBanner } from '../components/UsageWarningBanner';
import { SetupCard } from '../components/SetupCard';
import { CompetingPixelsBanner } from '../components/CompetingPixelsBanner';
import { BackfillHoldBanner } from '../components/BackfillHoldBanner';
import { formatMoney } from '../lib/money';

// The merchant's own revenue, in the merchant's own currency - a different
// axis from what WE charge. Both go through the shared helper, and both name
// their currency explicitly: the old default of 'EUR' here is exactly how one
// silently stood in for the other.
const formatCurrency = formatMoney;

function formatDate(dateString: string, locale = 'fr-FR'): string {
  return new Date(dateString).toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function StatCard({
  title,
  value,
  subtitle,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
}) {
  return (
    <Card>
      <BlockStack gap="100">
        <Text as="p" variant="bodyMd" tone="subdued">
          {title}
        </Text>
        <Text as="p" variant="headingXl" fontWeight="bold">
          {value}
        </Text>
        {subtitle && (
          <Text as="p" variant="bodySm" tone="subdued">
            {subtitle}
          </Text>
        )}
      </BlockStack>
    </Card>
  );
}

function IntegrationStatus({
  name,
  connected,
  status,
  errorMessage,
  t,
}: {
  name: string;
  connected: boolean;
  status: string;
  errorMessage?: string;
  t: ReturnType<typeof useTranslation>;
}) {
  const getBadge = () => {
    if (!connected) {
      return <Badge tone="attention">{t.common.notConnected}</Badge>;
    }
    switch (status) {
      case 'connected':
        return <Badge tone="success">{t.common.active}</Badge>;
      case 'warning':
        return <Badge tone="warning">{t.common.warning}</Badge>;
      case 'error':
        return <Badge tone="critical">{t.common.error}</Badge>;
      case 'disabled':
        return <Badge>{t.common.disabled}</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  return (
    <InlineStack align="space-between" blockAlign="center">
      <Text as="span" variant="bodyMd" fontWeight="semibold">
        {name}
      </Text>
      <InlineStack gap="200" blockAlign="center">
        {getBadge()}
        {errorMessage && (
          <Text as="span" variant="bodySm" tone="critical">
            {errorMessage}
          </Text>
        )}
      </InlineStack>
    </InlineStack>
  );
}

export function Dashboard() {
  const { data: dashboard, isLoading: dashboardLoading, error: dashboardError } = useDashboard();
  const { data: conversionsData } = useConversions(10);
  const { data: googleAds, error: googleAdsError } = useGoogleAdsConfig();
  const { data: meta, error: metaError } = useMetaConfig();
  const { data: ga4 } = useGA4Config();
  const { data: tiktok } = useTikTokConfig();
  const { data: pinterest } = usePinterestConfig();
  const { data: snapchat } = useSnapchatConfig();
  const t = useTranslation();
  const { language } = useLanguage();

  const isLoading = dashboardLoading;

  if (isLoading) {
    return (
      <Page title={t.dashboard.title}>
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={4} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (dashboardError) {
    return (
      <Page title={t.dashboard.title}>
        <Layout>
          <Layout.Section>
            <Banner tone="critical">
              <p>{t.common.error}</p>
            </Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const conversionsTableRows = (conversionsData?.conversions || []).map((c: any) => [
    c.orderName || c.orderId,
    formatCurrency(c.revenue, c.currency || dashboard?.currency || 'EUR', localeMap[language]),
    c.googleAdsTracked ? '\u2705' : '\u2014',
    c.metaTracked ? '\u2705' : '\u2014',
    c.ga4Tracked ? '\u2705' : '\u2014',
    c.tiktokTracked ? '\u2705' : '\u2014',
    c.pinterestTracked ? '\u2705' : '\u2014',
    c.snapchatTracked ? '\u2705' : '\u2014',
    // The order date, not the day Trackful recorded it (an imported order
    // would otherwise show the install day).
    formatDate(c.orderCreatedAt || c.createdAt, localeMap[language]),
  ]);

  return (
    <Page title={t.dashboard.title}>
      <Layout>
        {/* Usage Warning Banner */}
        <Layout.Section>
          <UsageWarningBanner />
        </Layout.Section>

        {/* Double-counting warning: a merchant staring at 2x conversions needs
            this ABOVE the numbers that are wrong, not buried under them. */}
        <Layout.Section>
          <CompetingPixelsBanner />
          <BackfillHoldBanner />
        </Layout.Section>

        {/* Turnkey setup / "all green" state - the onboarding lives here on the
            dashboard (no separate wizard). Flips to a single green card once the
            pixel emits and >=1 channel is live. */}
        <Layout.Section>
          <SetupCard />
        </Layout.Section>

        {/* Integration Load Errors */}
        {(googleAdsError || metaError) && (
          <Layout.Section>
            <Banner tone="warning">
              <p>{t.dashboard.integrationLoadError}</p>
            </Banner>
          </Layout.Section>
        )}

        {/* Stats */}
        <Layout.Section>
          <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
            <StatCard
              title={t.dashboard.totalConversions}
              value={dashboard?.conversions?.thisMonth || 0}
              subtitle={t.common.thisMonth}
            />
            <StatCard
              title={t.dashboard.totalRevenue}
              value={formatCurrency(dashboard?.revenue?.thisMonth || 0, dashboard?.currency || 'EUR', localeMap[language])}
              subtitle={t.common.thisMonth}
            />
            {/* Was labelled "Google Ads Tracked" while showing trackingRate,
                which counts conversions carrying ANY click id. It read 100%
                next to "Failed this month: 1" on a month where Google Ads had
                taken 9 of 10. Delivery is its own number now, and it is the
                exact complement of the failure card beside it. */}
            <StatCard
              title={t.dashboard.deliveryRate}
              value={dashboard?.deliveryRate == null ? '\u2014' : `${Math.round(dashboard.deliveryRate)}%`}
              subtitle={t.dashboard.ofConversions}
            />
            <StatCard
              title={t.dashboard.failedThisMonth}
              value={`${dashboard?.failed?.thisMonth || 0}`}
              subtitle={t.common.thisMonth}
            />
          </InlineGrid>
        </Layout.Section>

        {/* Attribution Rate */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.dashboard.attributionRate}
              </Text>
              <InlineStack gap="400" align="space-between" blockAlign="center">
                <BlockStack gap="100">
                  <Text as="p" variant="headingXl" fontWeight="bold">
                    {Math.round(dashboard?.trackingRate || 0)}%
                  </Text>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {t.dashboard.conversionsWithAttribution}
                  </Text>
                </BlockStack>
                <Box>
                  {(dashboard?.trackingRate || 0) >= 90 ? (
                    <Badge tone="success">{t.dashboard.excellent}</Badge>
                  ) : (dashboard?.trackingRate || 0) >= 70 ? (
                    <Badge tone="attention">{t.dashboard.good}</Badge>
                  ) : (
                    <Badge tone="critical">{t.dashboard.needsImprovement}</Badge>
                  )}
                </Box>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Integration Status */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.dashboard.integrationStatus}
              </Text>
              <BlockStack gap="300">
                <IntegrationStatus
                  name="Google Ads"
                  connected={googleAds?.connected || false}
                  status={googleAds?.status || 'disabled'}
                  errorMessage={googleAds?.lastErrorMessage}
                  t={t}
                />
                <IntegrationStatus
                  name="Meta CAPI"
                  connected={meta?.connected || false}
                  status={meta?.status || 'disabled'}
                  errorMessage={meta?.lastErrorMessage}
                  t={t}
                />
                <IntegrationStatus
                  name="GA4"
                  connected={ga4?.connected || false}
                  status={ga4?.status || 'disabled'}
                  errorMessage={ga4?.lastErrorMessage}
                  t={t}
                />
                <IntegrationStatus
                  name="TikTok"
                  connected={tiktok?.connected || false}
                  status={tiktok?.status || 'disabled'}
                  errorMessage={tiktok?.lastErrorMessage}
                  t={t}
                />
                <IntegrationStatus
                  name="Pinterest"
                  connected={pinterest?.connected || false}
                  status={pinterest?.status || 'disabled'}
                  errorMessage={pinterest?.lastErrorMessage}
                  t={t}
                />
                <IntegrationStatus
                  name="Snapchat"
                  connected={snapchat?.connected || false}
                  status={snapchat?.status || 'disabled'}
                  errorMessage={snapchat?.lastErrorMessage}
                  t={t}
                />
              </BlockStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* EMQ Status */}
        <Layout.Section variant="oneHalf">
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.dashboard.eventMatchQuality}
              </Text>
              {meta?.emqStatus ? (
                <BlockStack gap="200">
                  <InlineStack gap="200" align="space-between">
                    <Text as="p" variant="bodyMd">
                      {t.meta.overallScore}
                    </Text>
                    <Badge
                      tone={
                        meta.emqStatus.overall === 'Excellent'
                          ? 'success'
                          : meta.emqStatus.overall === 'Good'
                            ? 'info'
                            : 'warning'
                      }
                    >
                      {meta.emqStatus.overall}
                    </Badge>
                  </InlineStack>
                  {meta.emqStatus.recommendations?.length > 0 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      {meta.emqStatus.recommendations[0]}
                    </Text>
                  )}
                </BlockStack>
              ) : (
                <Text as="p" variant="bodySm" tone="subdued">
                  {t.dashboard.emqNotAvailable}
                </Text>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Recent Conversions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                {t.dashboard.recentConversions}
              </Text>
              {/* The check marks are DELIVERY status per channel, not an
                  exclusive attribution - merchants read them as attribution
                  without this line. */}
              <Text as="p" variant="bodySm" tone="subdued">
                {t.dashboard.deliveryPerChannel}
              </Text>
              {conversionsTableRows.length > 0 ? (
                <DataTable
                  columnContentTypes={[
                    'text',
                    'numeric',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                    'text',
                  ]}
                  headings={[
                    t.dashboard.order,
                    t.dashboard.revenue,
                    'Google',
                    'Meta',
                    'GA4',
                    'TikTok',
                    'Pin.',
                    'Snap.',
                    t.dashboard.date,
                  ]}
                  rows={conversionsTableRows}
                />
              ) : (
                <EmptyState
                  heading={t.dashboard.noConversions}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>{t.dashboard.noConversionsDesc}</p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
